import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { PlayArrow } from "@mui/icons-material";
import { Box, LinearProgress, Typography } from "@mui/material";
import { useSnackbar } from "notistack";

import { NavigationButton } from "ui";

import { selectVersionsByProjectId, useCopyVersionMutation, useLazyGetVersionsQuery } from "fond/api";
import { useTask } from "fond/hooks/useTask";
import { setVersion } from "fond/project/redux";
import CreateScenarioModal from "fond/project/versions/CreateScenarioModal";
import { Store } from "fond/types/store";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { Modal } from "fond/widgets";

interface AnalysisPanelProps {
  // Flag indicating if the editing is read only
  isReadOnly?: boolean;
}

const AnalysisPanel: React.FC<AnalysisPanelProps> = ({ isReadOnly }) => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector((state) => state.project.projectId);
  const versionId = useAppSelector((state) => state.project.versionId);
  const versions = useSelector((state: Store) => selectVersionsByProjectId(state, projectId));
  const [getVersions] = useLazyGetVersionsQuery();
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [isCreatingScenario, setIsCreatingScenario] = useState<boolean>(false);
  const [copyVersion] = useCopyVersionMutation();
  const { enqueueSnackbar } = useSnackbar();
  const [copyVersionTaskId, setCopyVersionTaskId] = useState<string | null>(null);
  const {
    task: copyVersionTask,
    isRunning: copyVersionRunning,
    isError: copyVersionError,
    isSuccess: copyVersionSuccess,
  } = useTask({ id: copyVersionTaskId });

  const handleOnSubmit = async (name: string) => {
    setIsCopying(true);
    setIsCreatingScenario(false);
    try {
      const task = await copyVersion({
        versionId,
        name,
      }).unwrap();
      setCopyVersionTaskId(task.ID);
    } catch (e) {
      enqueueSnackbar("Failed to create scenario", { variant: "error" });
    }
  };

  // Switch to the newly created scenario & close the modal.
  useEffect(() => {
    const handleEffect = async () => {
      if (!copyVersionTask || copyVersionRunning) {
        return;
      }

      if (copyVersionSuccess) {
        const { ids } = await getVersions(projectId).unwrap();
        dispatch(setVersion(ids[0]));
        enqueueSnackbar("Scenario successfully created");
        setIsCopying(false);
      }

      if (copyVersionError) {
        enqueueSnackbar("Failed to create scenario", { variant: "error" });
        setIsCopying(false);
      }
    };

    handleEffect();
  }, [
    copyVersionTask,
    copyVersionSuccess,
    copyVersionRunning,
    copyVersionError,
    dispatch,
    projectId,
    versionId,
    getVersions,
    setIsCopying,
    enqueueSnackbar,
  ]);

  return (
    <>
      <Typography fontWeight="500">New Scenarios</Typography>
      <Typography variant="caption">
        Adjust your build scope by excluding specific service locations and see the updated cost to serve analysis.
      </Typography>
      <NavigationButton
        data-testid="input-data-navigation"
        icon={<PlayArrow />}
        title="Run new scenario"
        onClick={() => setIsCreatingScenario(true)}
        sx={{ m: 0, mt: 1 }}
        disabled={isReadOnly || isCopying}
      />

      {isCreatingScenario && (
        <CreateScenarioModal
          onClose={() => {
            setIsCreatingScenario(false);
          }}
          onSubmit={handleOnSubmit}
          versionNames={versions?.map((version) => version.Name)}
        />
      )}

      {isCopying && (
        <Modal
          open
          header="Creating scenario"
          data-testid="creating-scenario-modal"
          content={
            <Box>
              <LinearProgress />
              <Typography sx={{ mt: 1.5, color: "text.secondary" }}>This might take a few minutes.</Typography>
            </Box>
          }
        />
      )}
    </>
  );
};

export default AnalysisPanel;
